/**
 * Dynamic Checkout Cart Attributes Fix
 * 
 * Ensures cart attributes (like shipping estimates) are properly set before
 * customers use dynamic checkout buttons (Shop Pay, Apple Pay, Google Pay, etc.)
 */

class DynamicCheckoutFix {
  constructor() {
    this.isSyncing = false;
    this.shouldBypassCartSubmitInterception = false;
    this.latestAdditionalAttributes = null;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.onReady());
      return;
    }
    this.onReady();
  }

  onReady() {
    this.setupCartCheckoutInterception();
    this.setupDynamicCheckoutInterception();
    this.proactiveInitialSync();
  }

  isCartPage() {
    return window.location.pathname.indexOf('/cart') === 0;
  }

  async proactiveInitialSync() {
    this.toggleDynamicCheckoutButtons(false);
    const fallbackTimer = window.setTimeout(() => this.toggleDynamicCheckoutButtons(true), 3000);

    try {
      await this.syncAttributesFromLatestCartState();
    } catch (_error) {
      // keep checkout available via fallback timer
    } finally {
      window.clearTimeout(fallbackTimer);
      this.toggleDynamicCheckoutButtons(true);
    }
  }

  setupCartCheckoutInterception() {
    const cartForm = document.getElementById('cart');
    if (!cartForm) return;

    cartForm.addEventListener(
      'submit',
      async (event) => {
        if (this.shouldBypassCartSubmitInterception) {
          this.shouldBypassCartSubmitInterception = false;
          return;
        }

        event.preventDefault();
        const submitter = event.submitter || null;

        try {
          await this.syncAttributesFromLatestCartState();
        } catch (_error) {
          this.showCartError('カートの更新に失敗しました。処理を継続します。');
        } finally {
          this.resumeCartSubmit(cartForm, submitter);
        }
      },
      true
    );
  }

  resumeCartSubmit(cartForm, submitter) {
    this.shouldBypassCartSubmitInterception = true;

    if (submitter && typeof cartForm.requestSubmit === 'function') {
      cartForm.requestSubmit(submitter);
      return;
    }

    if (submitter && submitter.name) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = submitter.name;
      hidden.value = submitter.value || '';
      hidden.dataset.dynamicCheckoutFallback = 'true';
      cartForm.appendChild(hidden);
    }

    cartForm.submit();

    const fallbackInput = cartForm.querySelector('input[data-dynamic-checkout-fallback="true"]');
    if (fallbackInput) fallbackInput.remove();
  }

  setupDynamicCheckoutInterception() {
    document.addEventListener(
      'click',
      async (event) => {
        const button = event.target.closest(
          '.additional-checkout-buttons button, .additional-checkout-buttons [role="button"], .shopify-payment-button button, shop-pay-wallet-button, shopify-apple-pay-button, shopify-google-pay-button, shopify-paypal-button'
        );

        if (!button || button.dataset.dynamicCheckoutSyncBypass === 'true') return;
        if (this.isSyncing) return;

        event.preventDefault();
        event.stopPropagation();

        this.toggleDynamicCheckoutButtons(false);

        try {
          await this.syncAttributesFromLatestCartState();
        } catch (_error) {
          // do not block checkout
        } finally {
          button.dataset.dynamicCheckoutSyncBypass = 'true';
          this.toggleDynamicCheckoutButtons(true);

          window.setTimeout(() => {
            button.click();
            window.setTimeout(() => {
              delete button.dataset.dynamicCheckoutSyncBypass;
            }, 300);
          }, 0);
        }
      },
      true
    );
  }

  toggleDynamicCheckoutButtons(disabled) {
    const nodes = document.querySelectorAll(
      '.additional-checkout-buttons button, .additional-checkout-buttons [role="button"], .shopify-payment-button button'
    );
    nodes.forEach((node) => {
      if ('disabled' in node) {
        node.disabled = disabled;
      }
      node.setAttribute('aria-disabled', String(disabled));
      node.style.pointerEvents = disabled ? 'none' : '';
      node.style.opacity = disabled ? '0.6' : '';
    });
  }

  async syncAttributesFromLatestCartState() {
    if (this.isSyncing) return;
    this.isSyncing = true;

    try {
      if (this.isCartPage()) {
        await this.refreshAdditionalAttributesFromServer();
      }
      await this.updateCartAttributes();
    } finally {
      this.isSyncing = false;
    }
  }

  async refreshAdditionalAttributesFromServer() {
    const response = await fetch(`/cart?_wl_sync_ts=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Failed to fetch latest cart page');

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const latestAttributes = {};
    const latestInputs = doc.querySelectorAll(
      'input[data-additional-attribute], select[data-additional-attribute], textarea[data-additional-attribute]'
    );
    latestInputs.forEach((input) => {
      const name = input.getAttribute('data-additional-attribute');
      if (!name) return;
      latestAttributes[name] = input.value || '';
    });

    if (Object.keys(latestAttributes).length === 0) {
      this.latestAdditionalAttributes = null;
      throw new Error('No additional attributes found in latest cart HTML');
    }

    this.latestAdditionalAttributes = latestAttributes;

    const latestShipping = latestAttributes['発送予定'];
    const currentInput = document.querySelector('input[data-additional-attribute="発送予定"]');
    if (currentInput && typeof latestShipping !== 'undefined') {
      currentInput.value = latestShipping;
      currentInput.setAttribute('value', latestShipping);
    }
  }

  async updateCartAttributes() {
    const attributes = this.collectCartAttributes();
    if (Object.keys(attributes).length === 0) return;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ attributes: attributes })
    });

    if (!response.ok) {
      throw new Error(`Cart update failed: ${response.status}`);
    }
  }

  collectCartAttributes() {
    const attributes = {};
    const inputs = document.querySelectorAll(
      'input[data-additional-attribute], select[data-additional-attribute], textarea[data-additional-attribute]'
    );

    inputs.forEach((input) => {
      const name = input.getAttribute('data-additional-attribute');
      if (!name) return;
      attributes[name] = input.value || '';
    });

    if (this.latestAdditionalAttributes) {
      Object.assign(attributes, this.latestAdditionalAttributes);
    }

    return attributes;
  }

  showCartError(message) {
    const errorDiv = document.getElementById('cart-errors');
    if (!errorDiv) return;
    errorDiv.textContent = message;
    errorDiv.classList.add('form__message--error');
  }
}

if (typeof window !== 'undefined') {
  window.dynamicCheckoutFix = new DynamicCheckoutFix();
}
